import {
  Comment,
  Discussion,
  Event,
  Identifiable,
  List,
  Milestone,
  Project,
  Task,
} from "@planzone/dto";
import { HttpClient, HttpMethods } from "@planzone/services";
import { LinkedResource } from "../linkers/types";
import { DiscussionSubjectTypes } from "../../dto/discussionSubjectTypes";

function fetchCommentsCount(
  httpClient: HttpClient,
  discussionUuid: string,
): Promise<number> {
  return httpClient
    .sendHttpRequest<{ page?: { totalElements: number } }>({
      endpoint: `/comments/search/findByDiscussion_Uuid?discussionUuid=${discussionUuid}&page=0&size=1`,
      method: HttpMethods.GET,
    })
    .then(({ page }) => page?.totalElements ?? 0);
}

function getDiscussions(httpClient: HttpClient) {
  return (project: Identifiable<Project>, page: number, size: number) =>
    httpClient
      .sendHttpRequest<{
        _embedded?: {
          discussions: LinkedResource<Identifiable<Discussion>>[];
        };
      }>({
        endpoint: `/discussions/search/findByProjectUuidOrderByLastCommentDateDesc?projectUuid=${project.uuid}&page=${page}&size=${size}`,
        method: HttpMethods.GET,
      })
      .then(({ _embedded }) => _embedded?.discussions ?? [])
      .then((discussions) =>
        discussions.map((discussion) =>
          fetchCommentsCount(httpClient, discussion.uuid).then(
            (commentsCount) => ({
              ...discussion,
              comments: [] as Identifiable<Comment>[],
              commentsCount,
            }),
          ),
        ),
      )
      .then((promises) => Promise.all(promises))
      .then((discussions) =>
        discussions.map((discussion) =>
          httpClient
            .sendHttpRequest<
              | Identifiable<Task>
              | Identifiable<List>
              | Identifiable<Milestone>
              | Identifiable<Event>
              | Identifiable<Project>
            >({
              endpoint: `/${
                discussion.entityType === DiscussionSubjectTypes.MILESTONE
                  ? "milestones"
                  : discussion.entityType === DiscussionSubjectTypes.LIST
                    ? "activities"
                    : discussion.entityType === DiscussionSubjectTypes.EVENT
                      ? "events"
                      : discussion.entityType === DiscussionSubjectTypes.PROJECT
                        ? "projects"
                        : "tasks"
              }/${discussion.entityUuid}?embed_links=false`,
              method: HttpMethods.GET,
            })
            .then(
              (subject) =>
                ({
                  ...discussion,
                  subject:
                    discussion.entityType === DiscussionSubjectTypes.MILESTONE
                      ? (subject as Identifiable<Milestone>)
                      : discussion.entityType === DiscussionSubjectTypes.LIST
                        ? (subject as Identifiable<List>)
                        : discussion.entityType === DiscussionSubjectTypes.EVENT
                          ? (subject as Identifiable<Event>)
                          : discussion.entityType ===
                              DiscussionSubjectTypes.PROJECT
                            ? (subject as Identifiable<Project>)
                            : (subject as Identifiable<Task>),
                }) as Identifiable<Discussion>,
            ),
        ),
      )
      .then((promises) => Promise.allSettled(promises))
      .then((values) => values.filter(({ status }) => status === "fulfilled"))
      .then((values) =>
        values.map((value) => ("value" in value ? value.value : null)),
      )
      .then(
        (discussions) =>
          discussions.filter(
            (discussion) => discussion !== null,
          ) as Identifiable<Discussion>[],
      );
}

export { getDiscussions };
